// StudentTable.js

import React, { useState } from "react";
import { Trash2, Edit } from "lucide-react";
import StudentProfileModal from "./StudentProfileModal"; // Adjust the import path if necessary
import Select from "react-select";

const calculateHours = (startTime, endTime) => {
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);

  const totalMinutes =
    endHour * 60 + endMinute - (startHour * 60 + startMinute);
  return totalMinutes / 60;
};

const calculateTotalApprovedHours = (timeLogs, studentId) => {
  const studentLogs = timeLogs.filter((log) => log.studentId === studentId);
  const total = studentLogs
    .filter((log) => log.status === "approved")
    .reduce((sum, log) => {
      const hours = calculateHours(log.startTime, log.endTime);
      return sum + hours;
    }, 0);

  return Number(total.toFixed(1));
};

const StudentTable = ({
  students,
  opportunities,
  timeLogs,
  onEditStudent,
  onDeleteStudent,
}) => {
  // State variables for filters
  const [selectedStudentFilter, setSelectedStudentFilter] = useState(null);
  const [filterProgramType, setFilterProgramType] = useState("");
  const [filterCohort, setFilterCohort] = useState("");

  // State variable for selected student to display in modal
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Get unique program types and cohorts for dropdown options
  const programTypes = [...new Set(students.map((student) => student.program))];
  const cohorts = [
    ...new Set(
      students.map((student) =>
        student.cohort !== undefined && student.cohort !== null
          ? String(student.cohort)
          : "N/A"
      )
    ),
  ].sort((a, b) => (a === "N/A" ? 1 : b === "N/A" ? -1 : a.localeCompare(b)));

  // Create options for the student select
  const studentOptions = students.map((student) => ({
    value: student.id,
    label: `${student.firstName} ${student.lastName} [${student.studentId}]`,
    student: student, // Keep the full student object for filtering
  }));

  // Filtered students based on filter criteria
  const filteredStudents = students.filter((student) => {
    const studentMatch = selectedStudentFilter
      ? student.id === selectedStudentFilter.value
      : true;
    const programMatch = filterProgramType
      ? student.program === filterProgramType
      : true;
    const cohortMatch = filterCohort
      ? (student.cohort !== undefined && student.cohort !== null
          ? String(student.cohort)
          : "N/A") === filterCohort
      : true;

    return studentMatch && programMatch && cohortMatch;
  });

  // Sort filtered students by last name
  const sortedStudents = filteredStudents.sort((a, b) => {
    return a.lastName.localeCompare(b.lastName);
  });

  return (
    <div className="mt-10">
      {/* Filter Inputs */}
      <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0 mb-4">
        {/* Student Name Filter */}
        <Select
          isClearable
          options={studentOptions}
          value={selectedStudentFilter}
          onChange={setSelectedStudentFilter}
          placeholder="Filter by Student Name"
          className="w-full md:w-1/3"
          classNamePrefix="select"
        />

        {/* Program Type Filter */}
        <select
          value={filterProgramType}
          onChange={(e) => setFilterProgramType(e.target.value)}
          className="border px-3 py-2 rounded w-full md:w-1/3 mt-4 md:mt-0"
        >
          <option value="">All Programs</option>
          {programTypes.map((program) => (
            <option key={program} value={program}>
              {program}
            </option>
          ))}
        </select>

        {/* Cohort Filter */}
        <select
          value={filterCohort}
          onChange={(e) => setFilterCohort(e.target.value)}
          className="border px-3 py-2 rounded w-full md:w-1/3 mt-4 md:mt-0"
        >
          <option value="">All Cohorts</option>
          {cohorts.map((cohort) => (
            <option key={cohort} value={cohort}>
              {cohort}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto -mx-4 md:mx-0">
        <div className="block md:hidden mb-4">
          {/* Mobile view cards */}
          {sortedStudents.length > 0 ? (
            sortedStudents.map((student) => (
              <div key={student.id} className="bg-white p-4 rounded-lg shadow mb-4 border">
                <div className="flex justify-between items-center mb-2">
                  <button
                    onClick={() => setSelectedStudent(student)}
                    className="text-blue-600 hover:text-blue-900 font-medium"
                  >
                    <span className="text-blue-500 hover:text-blue-700">
                      {student.firstName} {student.lastName}
                    </span>
                  </button>
                  <div className="flex space-x-2" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}>
                    <Edit
                      size={18}
                      className="text-blue-500 cursor-pointer hover:text-blue-600"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEditStudent(student);
                        setSelectedStudent(null);
                      }}
                    />
                    <Trash2
                      size={18}
                      className="text-gray-500 cursor-pointer hover:text-gray-600"
                      onClick={() => onDeleteStudent(student.id)}
                    />
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">ID:</span> {student.studentId}</p>
                  <p><span className="text-gray-500">Program:</span> {student.program || "N/A"}</p>
                  <p><span className="text-gray-500">Cohort:</span> {student.cohort || "N/A"}</p>
                  <p><span className="text-gray-500">Hours:</span> {calculateTotalApprovedHours(timeLogs, student.studentId)}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 py-4">No students found.</p>
          )}
        </div>
        <table className="hidden md:table min-w-full bg-white table-auto w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Student ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Student Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Program
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cohort
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedStudents.length > 0 ? (
              sortedStudents.map((student) => (
                <tr
                  key={`${student.id}-${student.studentId}`}
                  className="hover:bg-gray-100 transition duration-300"
                >
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 cursor-pointer"
                    onClick={() => setSelectedStudent(student)}
                  >
                    {student.studentId}
                  </td>
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm cursor-pointer"
                    onClick={() => setSelectedStudent(student)}
                  >
                    <button className="text-blue-600 hover:text-blue-800">
                      {`${student.firstName} ${student.lastName}`}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {student.program || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {student.cohort !== undefined
                      ? String(student.cohort)
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {calculateTotalApprovedHours(timeLogs, student.studentId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                        <button
                          className="p-1 rounded hover:bg-gray-200"
                          onClick={(e) => {
                            e.preventDefault();
                            onEditStudent(student);
                          }}
                        >
                          <Edit
                            size={18}
                            className="text-blue-500"
                          />
                        </button>
                      <Trash2
                        size={18}
                        className="text-gray-500 cursor-pointer hover:text-gray-600 transition-colors duration-200"
                        onClick={() => onDeleteStudent(student.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No students found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Student Profile Modal */}
      {selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          opportunities={opportunities}
          timeLogs={timeLogs}
        />
      )}
    </div>
  );
};

export default StudentTable;